import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Trash2, Mail, Shield, User } from "lucide-react";

interface TeamMember {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  role: string;
}

interface PendingInvite {
  id: string;
  email: string;
  role: string;
  created_at: string;
  status: string;
}

const TeamSettings = () => {
  const { profile, session } = useAuth();
  const { t } = useLanguage();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<string>("member");
  const [sending, setSending] = useState(false);

  const fetchTeamData = async () => {
    if (!profile?.organisation_id) return;
    setLoading(true);

    // Get profiles in the same org
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, user_id, first_name, last_name, email")
      .eq("organisation_id", profile.organisation_id);

    // Get roles for these users
    if (profiles && profiles.length > 0) {
      const userIds = profiles.map((p) => p.user_id);
      const { data: roles } = await supabase
        .from("user_roles")
        .select("user_id, role")
        .in("user_id", userIds);

      const roleMap: Record<string, string> = {};
      roles?.forEach((r) => {
        roleMap[r.user_id] = r.role;
      });

      setMembers(
        profiles.map((p) => ({
          ...p,
          role: roleMap[p.user_id] || "member",
        }))
      );
    }

    // Get pending invites
    const { data: inviteData } = await supabase
      .from("organisation_invites")
      .select("id, email, role, created_at, status")
      .eq("organisation_id", profile.organisation_id)
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    setInvites(inviteData || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchTeamData();
  }, [profile?.organisation_id]);

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setSending(true);

    try {
      const { data, error } = await supabase.functions.invoke("accept-invite", {
        body: { action: "send_invite", email: inviteEmail.trim().toLowerCase(), role: inviteRole },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(t("team.invite_sent"));
      setInviteEmail("");
      setInviteRole("member");
      fetchTeamData();
    } catch (err: any) {
      toast.error(err.message || t("team.invite_error"));
    } finally {
      setSending(false);
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    const { error } = await supabase
      .from("organisation_invites")
      .delete()
      .eq("id", inviteId);

    if (error) {
      toast.error(t("team.invite_error"));
    } else {
      toast.success(t("team.invite_cancelled"));
      fetchTeamData();
    }
  };

  if (loading) {
    return <div className="text-muted-foreground text-sm py-8 text-center">Loading…</div>;
  }

  const isAdmin = members.some(
    (m) => m.user_id === session?.user?.id && (m.role === "admin" || m.role === "super_admin")
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-bold text-foreground">{t("team.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("team.subtitle")}</p>
      </div>

      {/* Invite form - only for admins */}
      {isAdmin && (
        <div className="rounded-xl border border-border bg-card p-5 shadow-card space-y-4">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            <h2 className="text-sm font-semibold text-card-foreground">{t("team.invite")}</h2>
          </div>
          <p className="text-xs text-muted-foreground">{t("team.invite_desc")}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="invite-email" className="text-xs">{t("team.email")}</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-40 space-y-1.5">
              <Label className="text-xs">{t("team.role")}</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">{t("team.role_member")}</SelectItem>
                  <SelectItem value="admin">{t("team.role_admin")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={handleSendInvite} disabled={sending || !inviteEmail.trim()}>
                {sending ? t("team.sending") : t("team.send_invite")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Current members */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">{t("team.members")}</h2>
        {members.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("team.no_members")}</p>
        ) : (
          <div className="divide-y divide-border rounded-xl border border-border bg-card shadow-card overflow-hidden">
            {members.map((member) => (
              <div key={member.id} className="flex items-center justify-between px-5 py-3.5">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {member.role === "admin" || member.role === "super_admin" ? (
                      <Shield className="h-4 w-4" />
                    ) : (
                      <User className="h-4 w-4" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-card-foreground">
                      {[member.first_name, member.last_name].filter(Boolean).join(" ") || member.email}
                      {member.user_id === session?.user?.id && (
                        <span className="text-muted-foreground ml-1 font-normal">{t("team.you")}</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">{member.email}</p>
                  </div>
                </div>
                <Badge variant={member.role === "admin" || member.role === "super_admin" ? "default" : "secondary"}>
                  {member.role === "admin" || member.role === "super_admin"
                    ? t("team.role_admin")
                    : t("team.role_member")}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending invites */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">{t("team.pending_invites")}</h2>
        {invites.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("team.no_invites")}</p>
        ) : (
          <div className="divide-y divide-border rounded-xl border border-border bg-card shadow-card overflow-hidden">
            {invites.map((invite) => (
              <div key={invite.id} className="flex items-center justify-between px-5 py-3.5">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <Mail className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-card-foreground">{invite.email}</p>
                    <p className="text-xs text-muted-foreground">
                      {t("team.invited_on")} {new Date(invite.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">
                    {invite.role === "admin" ? t("team.role_admin") : t("team.role_member")}
                  </Badge>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => handleCancelInvite(invite.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamSettings;
